/**
 * `/gallery/day-week-strips` exists so a real browser can read the
 * accessibility nodes of a selected day chip and a selected week chip
 * (ISSUE-957 D3). It is signed in and linked from no navigation, following the
 * `/gallery/score-rows` precedent ISSUE-908 set for the same reason.
 *
 * WHY A GALLERY PAGE AND NOT THE REAL SCREEN. Neither strip is reachable in
 * the running e2e app today, measured rather than assumed:
 *   - `tests/e2e-harness/src/contract.ts:80-92` states these rows are the only
 *     sports and events data the harness seeds, and its `sportKey` is
 *     "football". `LeagueDrillDownPane.tsx` branches on
 *     `sport?.sportKey === WEEK_BASED_SPORT_KEY`, so the one seeded league
 *     renders the WEEK strip and `DateStrip` never paints at all.
 *   - `tests/e2e-harness/src/seedScores.ts` upserts that league's event with no
 *     `seasonWeek`, so the weeks read comes back empty and `WeekStrip` returns
 *     null on its own empty-list branch.
 * So in the harness as it stands, neither strip appears. Seeding a second
 * non-football league plus a season week crosses sports-api and events-api and
 * would need its own real-peer integration test, which is not a trade worth
 * making for an accessibility attribute fix.
 *
 * The file is named `gallery_.day-week-strips` so this route stays out of
 * `/gallery`'s tree: `/gallery` renders no Outlet, so a nested route would
 * never paint. Same trap `gallery_.marks.tsx` and `gallery_.score-rows.tsx`
 * both record.
 *
 * The selections are FIXED and the callbacks go nowhere. What the spec reads
 * is the accessibility node of an already selected chip, which does not depend
 * on selection being able to change, and a page that re-rendered on click
 * would let a stray click quietly move the chip the spec is reading.
 */
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { DateStrip } from "../features/scores/DateStrip";
import type { typeSeasonWeek } from "../features/scores/seasonWeek";
import { WeekStrip } from "../features/scores/WeekStrip";

/** A real drill-down day run: consecutive game days inside one month, the
 *  shape `LeagueDrillDownPane` hands the strip. */
const GALLERY_DAYS: readonly string[] = [
  "2026-09-10",
  "2026-09-11",
  "2026-09-12",
  "2026-09-13",
  "2026-09-14",
  "2026-09-17",
  "2026-09-18",
  "2026-09-19",
];

const SELECTED_DAY = "2026-09-13";
/** A fixed "today" inside the gallery's days, so the Today chip renders the
 *  same on every run instead of following the clock. */
const GALLERY_TODAY = "2026-09-12";

/** The calendar day `offset` days after Thursday 2026-09-03, "YYYY-MM-DD".
 *  Built through `Date.UTC` so a week past the end of September lands in
 *  October and November instead of on a "Sept 73" that no calendar has. */
function galleryDay(offset: number): string {
  return new Date(Date.UTC(2026, 8, 3 + offset)).toISOString().slice(0, 10);
}

/** Eighteen weeks, the length of a real football regular season and the
 *  "other seventeen" the card counts. A short list would not reproduce the
 *  horizontal scroll the selected chip is centered inside. Each week runs
 *  Thursday to Monday, seven days after the one before. */
const GALLERY_WEEKS: readonly typeSeasonWeek[] = Array.from(
  { length: 18 },
  (_unused, index): typeSeasonWeek => ({
    seasonWeek: index + 1,
    firstDay: galleryDay(index * 7),
    lastDay: galleryDay(index * 7 + 4),
  }),
);

const SELECTED_WEEK = 11;

/** This page has no drill-down list to filter, and the spec reads a chip that
 *  is already selected. The handlers exist because the props require them. */
const selectNothing = (): void => undefined;

const DayWeekStripsGalleryScreen = (): ReactNode => (
  <div className="flex flex-col gap-6 px-page-gutter py-4">
    <h1 className="text-h1 text-ink">Day and week strip gallery</h1>
    <p className="text-body text-ink-secondary">
      The day strip and the week strip, each with one chip already selected.
      This page exists so a browser can read what a screen reader is told about
      the selected chip once focus moves away. It is not linked from any
      navigation.
    </p>

    <div data-testid="day-strip-gallery" className="flex flex-col gap-2">
      <h2 className="text-h2 text-ink">Day strip</h2>
      <DateStrip
        days={GALLERY_DAYS}
        selectedDay={SELECTED_DAY}
        todayDay={GALLERY_TODAY}
        onSelectDay={selectNothing}
      />
    </div>

    <div data-testid="week-strip-gallery" className="flex flex-col gap-2">
      <h2 className="text-h2 text-ink">Week strip</h2>
      <WeekStrip
        weeks={GALLERY_WEEKS}
        selectedWeek={SELECTED_WEEK}
        onSelectWeek={selectNothing}
      />
    </div>

    {/* The spec moves focus here to reproduce the blur the card is about.
        It sits outside both strips on purpose: clicking a chip to blur
        another chip would change the selection being measured. */}
    <button
      type="button"
      data-testid="day-week-strips-neutral"
      className="self-start text-body text-ink-secondary underline"
      onClick={selectNothing}
    >
      Move focus off the strips
    </button>
  </div>
);

export const Route = createFileRoute("/gallery_/day-week-strips")({
  beforeLoad: requireSignedIn,
  component: DayWeekStripsGalleryScreen,
});
